import { Card, CardContent } from '@/components/ui/card';
import type { MensualObra } from '@adelante/shared';
import { CaretDown, CaretRight, DownloadSimple } from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { fmt } from './ComparativoProduccionCosto';

const NOMBRES_MES = [
  'Enero',
  'Febrero',
  'Marzo',
  'Abril',
  'Mayo',
  'Junio',
  'Julio',
  'Agosto',
  'Setiembre',
  'Octubre',
  'Noviembre',
  'Diciembre',
];

type Nivel = 'anio' | 'mes' | 'proyecto' | 'bloque' | 'obra';
type Agrupacion = 'tiempo' | 'obra';

/** Orden de los niveles del árbol según "Agrupar por". */
const NIVELES: Record<Agrupacion, Nivel[]> = {
  tiempo: ['anio', 'mes', 'proyecto', 'bloque', 'obra'],
  obra: ['proyecto', 'bloque', 'obra', 'anio', 'mes'],
};

interface Fila {
  worksNo: string;
  display: string | null;
  proyecto: string;
  bloque: string;
  anio: number;
  mes: number;
  produccion: number;
  costo: number;
}

interface Nodo {
  key: string;
  etiqueta: string;
  sublabel?: string | null;
  /** Valor para el orden natural (cronológico en año/mes, alfabético en el resto). */
  orden: number | string;
  produccion: number;
  costo: number;
  hijos: Nodo[];
}

function valorNivel(n: Nivel, f: Fila): { id: string; etiqueta: string; orden: number | string } {
  switch (n) {
    case 'anio':
      return { id: String(f.anio), etiqueta: String(f.anio), orden: f.anio };
    case 'mes':
      return { id: String(f.mes), etiqueta: NOMBRES_MES[f.mes - 1] ?? String(f.mes), orden: f.mes };
    case 'proyecto':
      return { id: f.proyecto, etiqueta: f.proyecto, orden: f.proyecto };
    case 'bloque':
      return { id: f.bloque, etiqueta: f.bloque, orden: f.bloque };
    case 'obra':
      return { id: f.worksNo, etiqueta: f.worksNo, orden: f.worksNo };
  }
}

/** Agrupa recursivamente las filas por los niveles dados, acumulando producción y costo. */
function construir(filas: Fila[], niveles: Nivel[], prefijo: string): Nodo[] {
  const [nivel, ...resto] = niveles;
  if (!nivel) return [];
  const grupos = new Map<string, { nodo: Nodo; filas: Fila[] }>();
  for (const f of filas) {
    const v = valorNivel(nivel, f);
    let g = grupos.get(v.id);
    if (!g) {
      g = {
        nodo: {
          key: `${prefijo}/${nivel}:${v.id}`,
          etiqueta: v.etiqueta,
          sublabel: nivel === 'obra' && f.display && f.display !== f.worksNo ? f.display : null,
          orden: v.orden,
          produccion: 0,
          costo: 0,
          hijos: [],
        },
        filas: [],
      };
      grupos.set(v.id, g);
    }
    g.nodo.produccion += f.produccion;
    g.nodo.costo += f.costo;
    g.filas.push(f);
  }
  const nodos = [...grupos.values()].map((g) => {
    g.nodo.hijos = construir(g.filas, resto, g.nodo.key);
    return g.nodo;
  });
  return nodos;
}

type ColumnaOrden = 'produccion' | 'costo' | 'dif' | 'pct';
const valorColumna = (n: Nodo, c: ColumnaOrden): number => {
  if (c === 'produccion') return n.produccion;
  if (c === 'costo') return n.costo;
  if (c === 'dif') return n.produccion - n.costo;
  // % de diferencia = diferencia ÷ producción (sin producción → al final).
  return n.produccion > 0 ? (n.produccion - n.costo) / n.produccion : Number.NEGATIVE_INFINITY;
};

function ordenar(nodos: Nodo[], col: { c: ColumnaOrden; dir: 1 | -1 } | null): Nodo[] {
  const copia = [...nodos].sort((a, b) => {
    if (col) return (valorColumna(a, col.c) - valorColumna(b, col.c)) * col.dir;
    return typeof a.orden === 'string'
      ? a.orden.localeCompare(String(b.orden))
      : a.orden - (b.orden as number);
  });
  return copia;
}

const pctDif = (n: { produccion: number; costo: number }) =>
  n.produccion > 0 ? `${(((n.produccion - n.costo) / n.produccion) * 100).toFixed(1)}%` : '—';

const ETIQUETA_NIVEL: Record<Nivel, string> = {
  anio: 'Año',
  mes: 'Mes',
  proyecto: 'Proyecto',
  bloque: 'Bloque',
  obra: 'Obra',
};

/**
 * Tabla en árbol Venta vs Costo. "Agrupar por" cambia el orden de los niveles:
 * Año → Mes → Proyecto → Bloque → Obra, o Proyecto → Bloque → Obra → Año → Mes.
 */
export function ArbolVentaCosto({ datos }: { datos: MensualObra[] }) {
  const [agrupar, setAgrupar] = useState<Agrupacion>('tiempo');
  const [abiertos, setAbiertos] = useState<Set<string>>(new Set());
  const [orden, setOrden] = useState<{ c: ColumnaOrden; dir: 1 | -1 } | null>(null);

  const filas = useMemo<Fila[]>(
    () =>
      datos
        .filter((f) => f.produccionMes !== 0 || (f.costoMes ?? 0) !== 0)
        .map((f) => ({
          worksNo: f.worksNo,
          display: f.displayName,
          proyecto: f.bloque ?? '(sin proyecto)',
          bloque: f.subBloque ?? '(sin bloque)',
          anio: f.anio,
          mes: f.mes,
          produccion: f.produccionMes,
          costo: f.costoMes ?? 0,
        })),
    [datos],
  );

  const niveles = NIVELES[agrupar];
  const arbol = useMemo(() => construir(filas, niveles, ''), [filas, niveles]);
  const total = useMemo(
    () => ({
      produccion: filas.reduce((a, f) => a + f.produccion, 0),
      costo: filas.reduce((a, f) => a + f.costo, 0),
    }),
    [filas],
  );

  const todasLasClaves = useMemo(() => {
    const k = new Set<string>();
    const rec = (ns: Nodo[], prof: number) => {
      for (const n of ns) {
        if (n.hijos.length > 0) {
          k.add(n.key);
          rec(n.hijos, prof + 1);
        }
      }
    };
    rec(arbol, 0);
    return k;
  }, [arbol]);

  const alternar = (key: string) =>
    setAbiertos((prev) => {
      const n = new Set(prev);
      if (n.has(key)) n.delete(key);
      else n.add(key);
      return n;
    });

  const cambiarAgrupacion = (a: Agrupacion) => {
    setAgrupar(a);
    setAbiertos(new Set());
  };

  const celda = 'px-2 py-1 text-right tabular-nums';
  const colorDif = (v: number) => (v < 0 ? 'text-red-600' : 'text-green-700');

  const renderNodos = (nodos: Nodo[], prof: number): React.ReactNode[] =>
    ordenar(nodos, orden).flatMap((n) => {
      const abierto = abiertos.has(n.key);
      const tieneHijos = n.hijos.length > 0;
      const dif = n.produccion - n.costo;
      const fila = (
        <tr
          key={n.key}
          className={`border-b border-zinc-100 ${prof === 0 ? 'bg-emerald-100 font-semibold' : prof === 1 ? 'bg-emerald-50 font-medium' : ''}`}
        >
          <td className="px-2 py-1" style={{ paddingLeft: 8 + prof * 18 }}>
            {tieneHijos ? (
              <button
                type="button"
                onClick={() => alternar(n.key)}
                className="flex items-center gap-1 text-left hover:underline"
              >
                {abierto ? (
                  <CaretDown className="h-3 w-3" weight="bold" />
                ) : (
                  <CaretRight className="h-3 w-3" weight="bold" />
                )}
                {n.etiqueta}
              </button>
            ) : (
              <div className="pl-4">
                <div>{n.etiqueta}</div>
                {n.sublabel && (
                  <div className="text-[10px] font-normal text-muted-foreground">{n.sublabel}</div>
                )}
              </div>
            )}
          </td>
          <td className={celda}>{fmt(n.produccion)}</td>
          <td className={celda}>{fmt(n.costo)}</td>
          <td className={`${celda} ${colorDif(dif)}`}>{fmt(dif)}</td>
          <td className={`${celda} ${colorDif(dif)}`}>{pctDif(n)}</td>
        </tr>
      );
      return abierto && tieneHijos ? [fila, ...renderNodos(n.hijos, prof + 1)] : [fila];
    });

  const th = (c: ColumnaOrden, texto: string) => (
    <th className={`${celda} font-medium`}>
      <button
        type="button"
        onClick={() =>
          setOrden((o) => (!o || o.c !== c ? { c, dir: -1 } : o.dir === -1 ? { c, dir: 1 } : null))
        }
        className="hover:underline"
      >
        {texto}
        {orden?.c === c ? (orden.dir === -1 ? ' ▼' : ' ▲') : ''}
      </button>
    </th>
  );

  const exportar = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Venta vs Costo');
    ws.addRow([
      niveles.map((n) => ETIQUETA_NIVEL[n]).join(' / '),
      'Producción',
      'Costo real',
      'Diferencia',
      '% Diferencia',
    ]).font = { bold: true };
    const rec = (nodos: Nodo[], prof: number) => {
      for (const n of ordenar(nodos, orden)) {
        const dif = n.produccion - n.costo;
        const r = ws.addRow([
          `${'   '.repeat(prof)}${n.etiqueta}${n.sublabel ? ` — ${n.sublabel}` : ''}`,
          n.produccion,
          n.costo,
          dif,
          n.produccion > 0 ? dif / n.produccion : null,
        ]);
        if (prof === 0) r.font = { bold: true };
        rec(n.hijos, prof + 1);
      }
    };
    rec(arbol, 0);
    const t = ws.addRow([
      'Total',
      total.produccion,
      total.costo,
      total.produccion - total.costo,
      total.produccion > 0 ? (total.produccion - total.costo) / total.produccion : null,
    ]);
    t.font = { bold: true };
    ws.getColumn(1).width = 44;
    for (const c of [2, 3, 4]) {
      ws.getColumn(c).width = 18;
      ws.getColumn(c).numFmt = '"₡"#,##0';
    }
    ws.getColumn(5).width = 14;
    ws.getColumn(5).numFmt = '0.0%';
    const buf = await wb.xlsx.writeBuffer();
    const url = URL.createObjectURL(
      new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      }),
    );
    const a = document.createElement('a');
    a.href = url;
    a.download = 'venta-vs-costo.xlsx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const dif = total.produccion - total.costo;
  return (
    <Card>
      <CardContent className="space-y-3 p-4">
        <div className="flex flex-wrap items-center gap-3 text-xs">
          <span className="text-muted-foreground">Agrupar por:</span>
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-300">
            {(
              [
                ['tiempo', 'Año › Mes › Proyecto › Obra'],
                ['obra', 'Proyecto › Obra › Año › Mes'],
              ] as const
            ).map(([v, texto]) => (
              <button
                key={v}
                type="button"
                onClick={() => cambiarAgrupacion(v)}
                className={`px-2.5 py-1 ${agrupar === v ? 'bg-emerald-700 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-100'}`}
              >
                {texto}
              </button>
            ))}
          </div>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setAbiertos(new Set(todasLasClaves))}
            className="text-blue-600 hover:underline"
          >
            Expandir todo
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={() => setAbiertos(new Set())}
            className="text-blue-600 hover:underline"
          >
            Colapsar
          </button>
          <span className="text-zinc-300">|</span>
          <button
            type="button"
            onClick={exportar}
            className="inline-flex items-center gap-1 rounded-md border border-emerald-700 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50"
          >
            <DownloadSimple className="h-3.5 w-3.5" weight="bold" />
            Exportar a Excel
          </button>
        </div>
        <div className="max-h-[calc(100vh-220px)] overflow-auto">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-background">
              <tr className="border-b text-muted-foreground">
                <th className="px-2 py-1 text-left font-medium">
                  {niveles.map((n) => ETIQUETA_NIVEL[n]).join(' / ')}
                </th>
                {th('produccion', 'Producción')}
                {th('costo', 'Costo real')}
                {th('dif', 'Diferencia')}
                {th('pct', '% Diferencia')}
              </tr>
            </thead>
            <tbody>
              {renderNodos(arbol, 0)}
              <tr className="sticky bottom-0 border-t-2 border-zinc-300 bg-zinc-100 font-bold">
                <td className="px-2 py-1">Total</td>
                <td className={celda}>{fmt(total.produccion)}</td>
                <td className={celda}>{fmt(total.costo)}</td>
                <td className={`${celda} ${colorDif(dif)}`}>{fmt(dif)}</td>
                <td className={`${celda} ${colorDif(dif)}`}>{pctDif(total)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
